import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, from } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

// Import API models and service
import {
  SpuControllerService,
  GoodsRequestDTO,
  GoodsPageReqDTO,
  StatusReqDTO,
  IdsReqDTO,
  AttrListRequestDTO,
  SpuAttributeValue,
  GoodsDetailsVO,
  ProductImageVO,
  SpuImageAltUpdateDTO,
  BASE_PATH as GOODS_SERVICE_BASE_PATH,
} from '@app/api/goods-service';

// Interim hand-authored types for the FAQ endpoints (not yet in the generated
// goods-service client): replace with the generated `SpuFaqVO`/`SpuFaqUpsertDTO`
// once `npm run api:generate` picks up the backend contract.
export interface SpuFaqVO {
  id?: number;
  questionVi: string;
  answerVi: string;
  questionEn?: string | null;
  answerEn?: string | null;
  sortOrder: number;
  active: boolean;
}

export interface SpuFaqUpsertDTO {
  id?: number | null;
  questionVi: string;
  answerVi: string;
  questionEn?: string | null;
  answerEn?: string | null;
  sortOrder: number;
  active: boolean;
}

// Import local interfaces
import {
  OrdinarySpuItem,
  PackageSpuItem,
  getDisplayTypeNumber,
  getSubmissionsTypeNumber,
} from '../spu.config';
import { SpuCategoryService } from './spu-category.service';
import { SpuBrandService } from './spu-brand.service';
import { SpuMaterialService } from './spu-material.service';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';

type GoodsRequestWithTax = Omit<GoodsRequestDTO, 'taxId' | 'isPriceIncludeVAT'> & {
  taxId?: number | null;
  isPriceIncludeVAT?: boolean | null;
};

// API Response interfaces
export interface SpuListResponse<T> {
  data: T[];
  totalRecords: number;
  loading: boolean;
}

export interface SpuSearchParams {
  name?: string;
  spuCode?: string;
  type?: string;
  categoryIdList?: string | string[] | any[]; // Allow array of objects from treeselect
  brandId?: string;
  materialName?: string;
  status?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SpuApiService {
  private translateService = inject(TranslateService);
  private httpClient = inject(HttpClient);
  private goodsServiceBasePath = inject(GOODS_SERVICE_BASE_PATH);

  constructor(
    private spuControllerService: SpuControllerService,
    private categoryService: SpuCategoryService,
    private brandService: SpuBrandService,
    private materialService: SpuMaterialService,
  ) {}

  // Create new SPU (Standard or Package Product
  createSpu(
    spuData: Partial<OrdinarySpuItem | PackageSpuItem>,
    isPackage: boolean,
  ): Observable<string> {
    const goodsRequest = this.mapToGoodsRequestDTO(
      spuData,
      isPackage,
    );
    return this.spuControllerService
      .createGoods(goodsRequest as unknown as GoodsRequestDTO, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === HttpStatusCode.Ok) return of('');

          throw error;
        }),
      );
  }

  // Update existing SPU
  updateSpu(
    spuData: Partial<OrdinarySpuItem | PackageSpuItem>,
    isPackage: boolean,
  ): Observable<string> {
    const goodsRequest = this.mapToGoodsRequestDTO(
      spuData,
      isPackage,
    );

    return this.spuControllerService
      .updateGoods(goodsRequest as unknown as GoodsRequestDTO, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === HttpStatusCode.Ok) return of('');

          throw error;
        }),
      );
  }

  // Get SPU by ID
  getSpuById(id: number): Observable<GoodsDetailsVO | null> {
    return this.spuControllerService.queryDetail(id).pipe(
      map((response: any) => {
        // Handle blob response
        if (response instanceof Blob) {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const jsonResponse = JSON.parse(reader.result as string);
                resolve(jsonResponse);
              } catch (error) {
                console.error('Failed to parse blob as JSON:', error);
                reject(error);
              }
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsText(response);
          });
        }
        return response;
      }),
      switchMap((result: any) => {
        if (result instanceof Promise) {
          return from(result);
        }
        return of(result);
      }),
      catchError((error) => {
        console.error('Error fetching SPU details:', error);
        return of(null);
      }),
    );
  }

  // Get SPU list with pagination and search
  getSpuList(
    page: number = 1,
    pageSize: number = 20,
    filters?: SpuSearchParams,
    isPackage: boolean = false,
  ): Observable<SpuListResponse<OrdinarySpuItem | PackageSpuItem>> {
    const goodsPageReq: GoodsPageReqDTO = {
      page: page - 1, // API uses 0-based pagination
      size: pageSize,
      name: filters?.name,
      spuCode: filters?.spuCode,
      type: filters?.type ? this.mapProductType(filters.type) : undefined,
      categoryIdList: this.buildCategoryIdList(filters?.categoryIdList),
      brandId: isPackage ? undefined : this.toNumber(filters?.brandId),
      status:
        filters?.status !== undefined && filters?.status !== null
          ? filters.status
            ? 1
            : 0
          : undefined,
      setFlag: isPackage ? 1 : 0,
    };
    return this.spuControllerService.pageGoods(goodsPageReq).pipe(
      map((response: any) => {
        // Handle blob response
        if (response instanceof Blob) {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const jsonResponse = JSON.parse(reader.result as string);
                resolve(this.processApiResponse(jsonResponse, isPackage));
              } catch (error) {
                reject(error);
              }
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsText(response);
          });
        }
        return this.processApiResponse(response, isPackage);
      }),
      switchMap((result: any) => {
        if (result instanceof Promise) {
          return from(result);
        }
        return of(result);
      }),
      catchError((error) => {
        console.error('Error fetching SPU list:', error);
        throw error;
      }),
    );
  }

  // Update SPU status
  updateSpuStatus(id: number, status: boolean): Observable<string> {
    const statusReq: StatusReqDTO = {
      ids: [id],
      status: status ? 1 : 0,
    };

    return this.spuControllerService.updateGoodsStatus(statusReq).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error) => {
        console.error('Error updating SPU status:', error);
        throw error;
      }),
    );
  }

  // Delete SPU
  deleteSpu(id: number): Observable<string> {
    const idsReq: IdsReqDTO = {
      ids: [id],
    };

    return this.spuControllerService.deleteByIds4(idsReq).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error) => {
        console.error('Error deleting SPU:', error);
        throw error;
      }),
    );
  }

  // Batch delete SPUs
  batchDeleteSpus(ids: number[]): Observable<string> {
    const idsReq: IdsReqDTO = {
      ids: ids,
    };

    return this.spuControllerService.deleteByIds4(idsReq).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error) => {
        console.error('Error batch deleting SPUs:', error);
        throw error;
      }),
    );
  }

  // Get SPU images (main/detail) with alt text metadata
  getSpuImages(spuId: number): Observable<ProductImageVO[]> {
    return this.spuControllerService.getImages1(spuId).pipe(
      map((response: any) => (Array.isArray(response) ? response : [])),
      catchError((error) => {
        console.error('Error fetching SPU images:', error);
        return of([]);
      }),
    );
  }

  // Update SPU image alt text (edit mode only; skipped on create)
  updateSpuImageAlt(
    spuId: number,
    items: SpuImageAltUpdateDTO[],
  ): Observable<void> {
    return this.spuControllerService.updateImageAltTexts(spuId, items).pipe(
      map(() => undefined),
      catchError((error) => {
        console.error('Error updating SPU image alt text:', error);
        throw error;
      }),
    );
  }

  // Errors propagate so the caller can distinguish "no FAQs yet" from "load
  // failed" instead of silently wiping data.
  getSpuFaqs(spuId: number): Observable<SpuFaqVO[]> {
    return this.httpClient
      .get<SpuFaqVO[]>(`${this.goodsServiceBasePath}/v1/goods/manager/${spuId}/faqs`)
      .pipe(map((response) => (Array.isArray(response) ? response : [])));
  }

  // Replace-set update of SPU FAQs (both create and edit flows, once spuId is known)
  updateSpuFaqs(spuId: number, items: SpuFaqUpsertDTO[]): Observable<void> {
    return this.httpClient
      .put<void>(`${this.goodsServiceBasePath}/v1/goods/manager/${spuId}/faqs`, items)
      .pipe(map(() => undefined));
  }

  // Helper method to map frontend data to API DTO
  private mapToGoodsRequestDTO(
    spuData: Partial<OrdinarySpuItem | PackageSpuItem>,
    isPackage: boolean,
  ): GoodsRequestWithTax {
    const categoryId = this.resolveCategoryId(spuData);
    if (!categoryId) {
      throw new Error('Product category is required');
    }

    return {
      id: spuData.id ? parseInt(spuData.id) : undefined,
      spuCode: spuData.spuCode || '',
      type: this.mapProductType(spuData.type),
      name: spuData.name || '',
      categoryId,
      additionalCategoryIds: this.resolveAdditionalCategoryIds(spuData),
      brandId: isPackage ? undefined : this.resolveBrandId(spuData),
      materialId: this.mapMaterialToId(spuData.materialName),
      remark: spuData.remark,
      status: spuData.status ? 1 : 0,
      setFlag: isPackage ? 1 : 0, // 0 = standard product, 1 = package
      taxId: spuData.taxId ?? null,
      isPriceIncludeVAT: spuData.isPriceIncludeVAT ?? null,
      attrs: this.mapPropertiesToAttrs(
        (spuData as any).properties || [],
        isPackage,
        !spuData.id,
      ),
    };
  }

  // Helper method to process API response
  private processApiResponse(
    response: any,
    isPackage: boolean,
  ): SpuListResponse<OrdinarySpuItem | PackageSpuItem> {
    if (!response) {
      return {
        data: [],
        totalRecords: 0,
        loading: false,
      };
    }

    // Check for both pageContent and content (for backward compatibility)
    const content = response.pageContent || response.content;

    if (!content) {
      return {
        data: [],
        totalRecords: 0,
        loading: false,
      };
    }

    const data = content.map((item: any) =>
      isPackage
        ? this.mapToPackageSpuItem(item)
        : this.mapToOrdinarySpuItem(item),
    );

    return {
      data: data,
      totalRecords: response.totalElements || 0,
      loading: false,
    };
  }

  // Helper method to map API response to OrdinarySpuItem
  private mapToOrdinarySpuItem(apiItem: any): OrdinarySpuItem {
    return {
      id: apiItem.id?.toString() || '',
      spuCode: apiItem.spuCode || '',
      name: apiItem.name || '',
      type: this.mapProductTypetoString(apiItem.type) || 'PHYSICAL',
      categoryName: apiItem.categoryName,
      brandName: apiItem.brandName,
      brandId: apiItem.brandId,
      materialName: apiItem.materialName,
      remark: apiItem.remark || '',
      status: apiItem.status === 1,
      taxOverrideMode:
        apiItem.taxId != null || apiItem.isPriceIncludeVAT != null
          ? 'OVERRIDE'
          : 'INHERIT',
      taxId: apiItem.taxId ?? null,
      isPriceIncludeVAT: apiItem.isPriceIncludeVAT ?? null,
      lastUpdateDate: apiItem.lastUpdateDate || apiItem.updatedAt || '',
    };
  }

  // Helper method to map API response to PackageSpuItem
  private mapToPackageSpuItem(apiItem: any): PackageSpuItem {
    return {
      id: apiItem.id?.toString() || '',
      spuCode: apiItem.spuCode || '',
      name: apiItem.name || '',
      type: this.mapProductTypetoString(apiItem.type) || 'PHYSICAL',
      categoryName: apiItem.categoryName,
      materialName: apiItem.materialName,
      remark: apiItem.remark || '',
      status: apiItem.status === 1,
      taxOverrideMode:
        apiItem.taxId != null || apiItem.isPriceIncludeVAT != null
          ? 'OVERRIDE'
          : 'INHERIT',
      taxId: apiItem.taxId ?? null,
      isPriceIncludeVAT: apiItem.isPriceIncludeVAT ?? null,
      packageItems: apiItem.packageItems || [],
    };
  }

  // Helper method to map properties to API attributes
  private mapPropertiesToAttrs(
    properties: any[],
    isPackage: boolean,
    isCreate: boolean,
  ): AttrListRequestDTO[] {
    if (!properties || !Array.isArray(properties)) {
      return [];
    }

    return properties.map((prop) => {
      // Ensure values is an array
      const values = Array.isArray(prop.values) ? prop.values : [];

      // Ensure generateSku is a boolean
      const generateSku =
        !isPackage &&
        (prop.generateSku === true ||
          prop.generateSku === 'Yes' ||
          prop.generateSku === 'true');

      const attr: AttrListRequestDTO = {
        id: isCreate ? undefined : prop.id,
        code: prop.code,
        attributeName: prop.name,
        remark: prop.remark,
        sort: prop.sort || 0,
        status: prop.status ? 1 : 0,
        type: getDisplayTypeNumber(prop.type), // Convert label to number
        submissionsType: getSubmissionsTypeNumber(
          prop.submissionsType,
          this.translateService,
        ), // Convert label to number
        isSku: generateSku,
        values: values.map(
          (val: any) =>
            ({
              id: isCreate ? undefined : val.id,
              value: val.value,
              sort: val.sort || 0,
              deleteFlag: 0,
              imageUrl: val.imageUrl || '', // Include imageUrl field
            }) as SpuAttributeValue,
        ),
      };
      return attr;
    });
  }

  // Helper method for mapping product type
  private mapProductType(type: string | number | undefined): number {
    if (typeof type === 'number') {
      return type;
    }
    // Handle string types if needed
    return type === 'VIRTUAL' ? 2 : 1; // Default to PHYSICAL (1)
  }

  private mapProductTypetoString(type: string | number | undefined): string {
    if (typeof type === 'string') {
      return type;
    }
    return type === 2 ? 'Virtual Product' : 'Physical Product'; // Default to PHYSICAL (1)
  }

  private buildCategoryIdList(
    categoryIdList?: string | string[] | any[],
  ): number[] | undefined {
    if (!categoryIdList) return undefined; // Return undefined if no category is provided

    // Handle different input types: string, array of strings, or array of objects
    let categoryIds: string[] = [];

    if (Array.isArray(categoryIdList)) {
      // Handle array of objects (from treeselect) or array of strings
      categoryIds = categoryIdList.map((item) => {
        if (typeof item === 'string') {
          return item;
        } else if (typeof item === 'object' && item.value) {
          return item.value; // Extract value from treeselect object
        }
        return String(item);
      });
    } else {
      // Handle single string
      categoryIds = [String(categoryIdList)];
    }

    // Convert string IDs to numbers and filter out invalid ones
    const numericIds = categoryIds
      .map((id) => parseInt(String(id)))
      .filter((id) => !isNaN(id));

    return numericIds.length > 0 ? numericIds : undefined;
  }

  // Additional (secondary) category ids are already resolved to plain numbers
  // by SpuFormWrapperComponent.prepareSpuData(); just pass them through.
  private resolveAdditionalCategoryIds(
    spuData: Partial<OrdinarySpuItem | PackageSpuItem>,
  ): number[] | undefined {
    const ids = (spuData as any).additionalCategoryIds;
    if (!Array.isArray(ids) || ids.length === 0) return undefined;

    const numericIds = ids
      .map((id: unknown) => Number(id))
      .filter((id: number) => Number.isFinite(id));

    return numericIds.length > 0 ? numericIds : undefined;
  }

  private resolveCategoryId(
    spuData: Partial<OrdinarySpuItem | PackageSpuItem>,
  ): number | undefined {
    if (spuData.categoryId) return Number(spuData.categoryId);

    const categoryValue = (spuData as any).categoryName;
    if (!categoryValue) return undefined;

    if (typeof categoryValue === 'object' && categoryValue.value !== undefined) {
      return this.toNumber(categoryValue.value);
    }

    return this.mapCategoryToId(String(categoryValue));
  }

  private mapCategoryToId(categoryName?: string): number | undefined {
    if (!categoryName) return undefined; // Return undefined if no category is provided

    // Try to get category ID from cached categories
    const categoryId = this.categoryService.getCategoryIdByName(categoryName);

    return categoryId; // Return undefined if not found, don't default to 1
  }

  private resolveBrandId(
    spuData: Partial<OrdinarySpuItem | PackageSpuItem>,
  ): number | undefined {
    const brandId = (spuData as OrdinarySpuItem).brandId;
    if (brandId) return this.toNumber(brandId);

    return this.mapBrandToId((spuData as OrdinarySpuItem).brandName);
  }

  private mapBrandToId(brandName?: string): number | undefined {
    if (!brandName) return undefined;

    const brand = this.brandService.getBrandByName(brandName);
    return brand ? brand.id : undefined;
  }

  private toNumber(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private mapMaterialToId(materialName?: string): number | undefined {
    if (!materialName) return undefined;

    const material = this.materialService.getMaterialByName(materialName);
    return material ? parseInt(material.id) : undefined;
  }
}
